import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom'

import BusinessEntitiesPage from './pages/Administration/BusinessEntities/BusinessEntitiesPage.jsx'
import BusinessEntity from './pages/BusinessEntity/BusinessEntity.jsx'
import ConfigTemplateDetail from './pages/ConfigTemplates/ConfigTemplate.jsx'
import ConfigTemplateList from './pages/ConfigTemplates/ConfigTemplateList.jsx'
import Dashboard from './pages/Dashboard/Dashboard.jsx'
import Devices from './pages/Devices/Devices.jsx'
import EditSystemUserPermissionsPage from './pages/Administration/SystemUsers/EditSystemUserPermissionsPage.jsx'
import EditSystemUserProfilePage from './pages/Administration/SystemUsers/EditSystemUserProfilePage.jsx'
import EditSystemUserRolesPage from './pages/Administration/SystemUsers/EditSystemUserRolesPage.jsx'
import EditUserPermissions from './pages/Administration/Users/EditUserPermissions.jsx'
import EditUserProfile from './pages/Administration/Users/EditUserProfile.jsx'
import EditUserRoles from './pages/Administration/Users/EditUserRoles.jsx'
import ErrorPage from './pages/Error/error-page.jsx'
import Forms from './pages/Forms/Forms.jsx'
import GroupDetailPage from './pages/Administration/Groups/GroupDetailPage'
import GroupsPage from './pages/Administration/Groups/GroupsPage.jsx'
import Home from './pages/Home/Home.jsx'
import InstallationList from './pages/Installations/InstallationList.jsx'
import MyProfilePage from './pages/MyProfile/MyProfilePage.jsx'
import PrivateRoute from './components/PrivateRoute.jsx'
import ProfileApiInterfaceDetail from './pages/Profiles/ProfileApiInterfaceDetail.jsx'
import ProfileApiInterfaceList from './pages/Profiles/ProfileApiInterfaceList.jsx'
import ProfileConfig from './pages/Profiles/ProfileConfig.jsx'
import ProfileDetail from './pages/Profiles/ProfileDetail.jsx'
import ProfileDeviceDetail from './pages/Profiles/ProfileDeviceDetail.jsx'
import ProfileDeviceList from './pages/Profiles/ProfileDeviceList.jsx'
import ProfileList from './pages/Profiles/ProfileList.jsx'
import ProfileRawEdit from './pages/Profiles/ProfileRawEdit.jsx'
import RoleDetailPage from './pages/Administration/Roles/RoleDetailPage'
import RolesPage from './pages/Administration/Roles/RolesPage.jsx'
import SystemUserProfilePage from './pages/Administration/SystemUsers/SystemUserProfilePage.jsx'
import SystemUsersPage from './pages/Administration/SystemUsers/SystemUsersPage.jsx'
import TasksList from './pages/Tasks/TasksList.jsx'
import UserList from './pages/Administration/Users/UserList.jsx'
import UtgApiInterfaceDetail from './pages/Utgs/UtgApiInterfaceDetail.jsx'
import UtgApiInterfaceList from './pages/Utgs/UtgApiInterfaceList.jsx'
import UtgConfig from './pages/Utgs/UtgConfig.jsx'
import UtgDetail from './pages/Utgs/UtgDetail.jsx'
import UtgDeviceDetail from './pages/Utgs/UtgDeviceDetail.jsx'
import UtgDeviceList from './pages/Utgs/UtgDeviceList.jsx'
import UtgList from './pages/Utgs/UtgList.jsx'

import { fetchAppConfig } from './state/actions/appConfigActions'
import { fetchPermissionDefinitions, fetchUserPermissionMasks } from './state/actions/permissionActions'
import { fetchSessionFromAuthCode, fetchSessionValidity } from './state/actions/sessionActions'
import { fetchUserByAccessToken } from './state/actions/userActions'

import { appConfigSignal } from './state/signals/appConfigSignals.js'
import { permissionDefinitionsSignal, userPermissionMasksSignal } from './state/signals/permissionSignals.js'
import { sessionSignal } from './state/signals/sessionSignals.js'
import { currentUserSignal } from './state/signals/userSignals.js'

import './assets/favicon.ico'
import './scss/betree.scss'
import './scss/profile.scss'
import './scss/sidebar.scss'
import './scss/styles.scss'
import './scss/userbox.scss'
import { parseString } from './utils/utils.js'
import useScript from './hooks/useScript.js'

let error

// The root loader handles getting the accessToken from SSO redirect and loads the user manifest as needed.
// This data is passed to our PrivateRoute component which then sets it as outlet context data for use in child routes
const rootLoader = async (request) => {
    let print = false
    // load application config data
    const config = await fetchAppConfig()
    const url = new URL(request.request.url)
    const authCode = url.searchParams.get('authcode')
    if (authCode) {
        // fetch and store accessToken if the user is being redirected from SSO
        await fetchSessionFromAuthCode(config, authCode).then(
            (session) => {
                if (print) {
                    console.log('storing accessToken')
                }
                localStorage.setItem('accessToken', session.accessToken)
            }
        )
    }
    else if (print) {
        console.log("auth code was not defined")
    }
    // load accessToken if its in local storage
    let storedToken = parseString(localStorage.getItem('accessToken'))
    if (!storedToken) {
        if (print) {
            console.log("stored token returned null, user is not logged in")
        }
        // user not logged in. Redirect to SSO or for local debugging, throw error to get redirect link so we can manually set accessToken in localStorage
        if (request.request.url.includes('localhost')) {
            error = new Error("Undefined access token")
            error.code = 401
            throw error
        }
        const url = `${config.ssoBaseUrl}?clientid=4&redirectToReferer=true`
        window.location.href = url
    }
    else if (print) {
        console.log("Stored token is ".concat(storedToken.toString()).concat(" of type ").concat(typeof storedToken))
    }
    // validate accessToken
    return await fetchSessionValidity(config, storedToken).then(
        async (session) => {
            if (!session) {
                if (print) {
                    console.log("Access token has expired")
                }
                // token is no longer valid
                //localStorage.removeItem('accessToken')
                localStorage.removeItem('selectedBusinessEntityId')
                //const url = `${config.ssoBaseUrl}?clientid=4&redirectToReferer=true`
                //window.location.href = url
                const error = Error("You are not logged in, or your session has expired")
                error.code = "401"
                throw error
            }
            else if (print) {
                console.log("Access token is still valid, about to log the session object")
                console.log(session)
            }
            if (!("accessToken" in session)) {
                throw Error("Session object does not have a field \"accessToken\"")
            }
            localStorage.setItem('accessToken', session.accessToken)
            // load user object from accessToken
            const user = await fetchUserByAccessToken(config, session.accessToken)
            if (!user) {
                throw new Error("fetchUserByAccessToken returned null")
            }
            else if (!("id" in user)) {
                const err = "User fetched from fetchUserByAccessToken lacks an ID property"
                console.error(err)
                console.log(user)
                throw new Error(err)

            }
            // If a different user is logging in, reset the cached business entity so they land on their own default
            const lastUserId = localStorage.getItem('lastUserId')
            if (lastUserId && lastUserId !== String(user.id)) {
                localStorage.removeItem('selectedBusinessEntityId')
            }
            localStorage.setItem('lastUserId', String(user.id))
            // load permissions
            const permissionDefinitions = await fetchPermissionDefinitions(config, session.accessToken)
            // load user permission mask
            return await fetchUserPermissionMasks(user.id, config, session.accessToken).then(
                () => {
                    return {
                        config: appConfigSignal.value,
                        accessToken: sessionSignal.value.accessToken,
                        user: currentUserSignal.value,
                        permissionMasks: userPermissionMasksSignal.value,
                        permissions: permissionDefinitionsSignal.value,
                        error: null
                    }
                }
            )
        }
    )
}

const JsonFormLoader = () => {
    useScript('/jsonform/deps/jquery.min.js')
    useScript('/jsonform/deps/opt/jquery.ui.widget.js')
    useScript('/jsonform/deps/opt/jquery.ui.core.js')
    useScript('/jsonform/deps/opt/jquery.ui.mouse.js')
    useScript('/jsonform/deps/opt/jquery.ui.sortable.js')
    useScript('/jsonform/deps/opt/jquery-ui.js')
    useScript('/jsonform/deps/opt/jquery.transloadit2.js')
    useScript('/jsonform/deps/underscore.js')
    useScript('/jsonform/deps/opt/bootstrap-dropdown.js')
    useScript('/jsonform/deps/opt/jsv.js')
    useScript('/jsonform/lib/jsonform-defaults.js')
    useScript('/jsonform/lib/jsonform-split.js')
    useScript('/jsonform/lib/jsonform.js')
    useScript('/jsonform/lib/pubsub.js')
    useScript('/jsonform/lib/shift4-utils.js')
}

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route loader={rootLoader} errorElement={<ErrorPage error={error}></ErrorPage>} element={<PrivateRoute />}>
            <Route path="/" element={<Home />} />
            <Route path="be/:beId" element={<BusinessEntity />} >
                <Route path="dashboard" element={<Dashboard />} handle='dashboard' />
                <Route path="utgs" element={<UtgList />} handle='utgs' />
                <Route path="utgs/:componentId" handle='utgs' >
                    <Route path="" element={<UtgDetail />} />
                    <Route path="apiinterfaces" element={<UtgApiInterfaceList />} />
                    <Route path="apiinterfaces/:id" element={<UtgApiInterfaceDetail />} />
                    <Route path="devices" element={<UtgDeviceList />} />
                    <Route path="devices/:name" element={<UtgDeviceDetail />} />
                    <Route path="config" element={<UtgConfig />} />
                </Route>
                <Route path="installations" element={<InstallationList />} handle='installations' />
                <Route path="management" handle='management'>
                    <Route path="accounts" element={<BusinessEntitiesPage />} />
                    <Route path="groups" element={<GroupsPage />} />
                    <Route path="groups/:groupId" element={<GroupDetailPage />} />
                    <Route path="roles" element={<RolesPage />} />
                    <Route path="roles/:roleId" element={<RoleDetailPage />} />
                    <Route path="users" element={<UserList />} handle='users' />
                    <Route path="users/:userId" handle="users">
                        <Route path="" element={<EditUserProfile />} />
                        <Route path="roles" element={<EditUserRoles />} />
                        <Route path="permissions" element={<EditUserPermissions />} />
                    </Route>
                    <Route path="systemUsers/:systemUserType" element={<SystemUsersPage />} />
                    <Route path="systemUsers/:systemUserType/:userId" handle="systemUsers">
                        <Route path="" element={<SystemUserProfilePage />} />
                        <Route path="permissions" element={<EditSystemUserPermissionsPage />} />
                    </Route>
                </Route>
                <Route path="profile" element={<MyProfilePage />} handle="profile" />
                <Route path="profiles" element={<ProfileList />} handle='profiles' />
                <Route path="profiles/:profileId" handle='profiles'>
                    <Route path="" element={<ProfileDetail />} />
                    <Route path="apiinterfaces" element={<ProfileApiInterfaceList />} />
                    <Route path="apiinterfaces/:id" element={<ProfileApiInterfaceDetail />} />
                    <Route path="devices" element={<ProfileDeviceList />} />
                    <Route path="devices/:name" element={<ProfileDeviceDetail />} />
                    <Route path="config" element={<ProfileConfig />} />
                    <Route path="edit" element={<ProfileRawEdit />} />
                </Route>
                <Route path="templates" element={<ConfigTemplateList />} handle='templates' />
                <Route path="templates/:configTemplateId" element={<ConfigTemplateDetail />} handle='templates' />
                <Route path="devices" element={<Devices />} handle="devices" />
                <Route path="forms" element={<Forms />} handle="formmanager" />
                <Route path="tasks" element={<TasksList />} handle='tasks' />
            </Route>
        </Route>
    ), {
        future: {
            v7_startTransition: true
        }
    }
)
ReactDOM.createRoot(document.getElementById('root')).render(
    <>
        <JsonFormLoader></JsonFormLoader>
        <RouterProvider router={router} />
    </>
)